/**
 * Samples text into a grid of points, by drawing it to an offscreen canvas once
 * and reading back the pixels that landed inside the letterforms.
 *
 * This is how the digits get their shape without shipping a path for them: the
 * type is whatever the browser renders, so the target points match the site's
 * font rather than a traced approximation of it.
 *
 * Returns [] during SSR and if the canvas is unavailable, and every caller
 * treats an empty target set as "draw nothing" rather than crashing.
 */
export interface GlyphOptions {
  text: string
  width: number
  height: number
  /** px between samples. Smaller is denser and costs more elements. */
  step?: number
  fontSize?: number
  fontWeight?: number
  /** Fraction of the canvas width the text is centred on. */
  cx?: number
  cy?: number
}

export const sampleGlyph = ({
  text,
  width,
  height,
  step = 7,
  fontSize = 210,
  fontWeight = 700,
  cx = 0.5,
  cy = 0.5,
}: GlyphOptions): Array<[number, number]> => {
  if (typeof document === "undefined") return []
  const c = document.createElement("canvas")
  c.width = width
  c.height = height
  const g = c.getContext("2d", { willReadFrequently: true })
  if (!g) return []
  g.fillStyle = "#fff"
  g.font = `${fontWeight} ${fontSize}px Inter, system-ui, -apple-system, sans-serif`
  g.textAlign = "center"
  g.textBaseline = "middle"
  g.fillText(text, width * cx, height * cy)
  const img = g.getImageData(0, 0, width, height).data
  const pts: Array<[number, number]> = []
  // offset the scan by half a step so the sample grid is not flush with the edge
  for (let y = step >> 1; y < height; y += step) {
    for (let x = step >> 1; x < width; x += step) {
      if (img[(y * width + x) * 4 + 3] > 128) pts.push([x, y])
    }
  }
  return pts
}
